import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import TechnicianFormDialog from "@/components/TechnicianFormDialog";
import SupplierFormDialog from "@/components/SupplierFormDialog";
import TechContactCard from "@/components/TechContactCard";
import ManageTechnicianSpecialitiesDialog from "@/components/ManageTechnicianSpecialitiesDialog";
import ExportButtons from "@/components/ExportButtons";
import {
  HardHat,
  Plus,
  Search,
  Phone,
  Mail,
  Edit3,
  Building2,
  Cog,
  Clock,
  Settings2,
} from "lucide-react";

const Technicians = () => {
  const { isManager } = useAuth();
  const [techs, setTechs] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState("all");
  const [specFilter, setSpecFilter] = useState("all");
  const [techFormOpen, setTechFormOpen] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [orgFormOpen, setOrgFormOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [contactFor, setContactFor] = useState(null); // {technicianId}
  const [manageSpecOpen, setManageSpecOpen] = useState(false);
  // Managed list of specialities (admin-curated). Filter chips draw from
  // this list — union'd with anything currently tagged on a technician so
  // legacy free-text still shows up.
  const [managedSpecs, setManagedSpecs] = useState([]);

  const load = async () => {
    try {
      const [t, o, s] = await Promise.all([
        api.get("/technicians"),
        api.get("/suppliers"),
        api.get("/technician-specialities").catch(() => ({ data: [] })),
      ]);
      // Compute per-supplier tech counts on the client so we don't need a
      // dedicated endpoint (suppliers already carry item + spend counts).
      const counts = {};
      (t.data || []).forEach((x) => {
        if (x.supplier_id) counts[x.supplier_id] = (counts[x.supplier_id] || 0) + 1;
      });
      setTechs(t.data);
      setOrgs((o.data || []).map((s) => ({ ...s, tech_count: counts[s.id] || 0 })));
      setManagedSpecs(s.data || []);
    } catch {
      toast.error("Could not load technicians");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const specialities = useMemo(() => {
    // Union of admin-managed list + anything currently tagged on techs, so
    // legacy free-text still shows as a filter chip until someone tidies
    // it via Manage lists.
    const seen = new Set(managedSpecs.map((s) => s.name));
    techs.forEach((t) => (t.specialities || []).forEach((s) => seen.add(s)));
    return Array.from(seen).sort();
  }, [techs, managedSpecs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return techs.filter((t) => {
      if (orgFilter !== "all" && (t.supplier_id || "_none") !== orgFilter) {
        return false;
      }
      if (specFilter !== "all" && !(t.specialities || []).includes(specFilter)) {
        return false;
      }
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        (t.supplier_name || "").toLowerCase().includes(q) ||
        (t.role || "").toLowerCase().includes(q) ||
        (t.email || "").toLowerCase().includes(q) ||
        (t.phone || "").toLowerCase().includes(q) ||
        (t.specialities || []).some((s) => s.toLowerCase().includes(q))
      );
    });
  }, [techs, search, orgFilter, specFilter]);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="technicians-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <HardHat size={14} strokeWidth={2.5} />
            Service contacts · linked machines
          </div>
          <h1 className="font-display text-5xl leading-none mt-2">Technicians</h1>
        </div>
        {isManager && (
          <div className="flex items-center gap-2 flex-wrap">
            <ExportButtons
              entity="technicians"
              rows={techs}
              testidPrefix="tech-export"
            />
            <button
              data-testid="tech-manage-specialities-btn"
              onClick={() => setManageSpecOpen(true)}
              className="nb-btn bg-white px-4 py-3 flex items-center gap-2"
              title="Manage the list of specialities technicians can be tagged with"
            >
              <Settings2 size={16} strokeWidth={2.5} /> Specialities
            </button>
            <button
              data-testid="tech-manage-orgs-btn"
              onClick={() => {
                setEditingOrg(null);
                setOrgFormOpen(true);
              }}
              className="nb-btn bg-white px-4 py-3 flex items-center gap-2"
            >
              <Building2 size={16} strokeWidth={2.5} /> Add supplier
            </button>
            <button
              data-testid="tech-add-btn"
              onClick={() => {
                setEditingTech(null);
                setTechFormOpen(true);
              }}
              className="nb-btn nb-btn-primary px-5 py-4 flex items-center gap-2"
            >
              <Plus size={20} strokeWidth={2.5} /> Add technician
            </button>
          </div>
        )}
      </div>

      {/* Suppliers strip */}
      {orgs.length > 0 && (
        <div className="mb-4">
          <div className="label-eyebrow mb-2">Suppliers</div>
          <div className="flex flex-wrap gap-2" data-testid="tech-org-chips">
            <button
              data-testid="tech-org-chip-all"
              onClick={() => setOrgFilter("all")}
              className={`nb-btn px-3 py-1.5 text-sm ${
                orgFilter === "all" ? "nb-btn-primary" : "bg-white"
              }`}
            >
              All ({techs.length})
            </button>
            {orgs.map((o) => (
              <div
                key={o.id}
                className={`inline-flex items-center border-2 border-black rounded-lg overflow-hidden ${
                  orgFilter === o.id ? "shadow-[3px_3px_0_0_#000]" : ""
                }`}
              >
                <button
                  data-testid={`tech-org-chip-${o.id}`}
                  onClick={() => setOrgFilter(o.id)}
                  className={`px-3 py-1.5 text-sm font-bold ${
                    orgFilter === o.id ? "bg-[#FFB300]" : "bg-white"
                  }`}
                >
                  {o.name}{" "}
                  <span className="text-[10px] text-[#607087] ml-1">
                    ({o.tech_count})
                  </span>
                </button>
                {isManager && (
                  <button
                    data-testid={`tech-org-edit-${o.id}`}
                    onClick={() => {
                      setEditingOrg(o);
                      setOrgFormOpen(true);
                    }}
                    className="px-2 py-1.5 border-l-2 border-black bg-white hover:bg-[#EAF5FB]"
                    aria-label={`edit ${o.name}`}
                  >
                    <Edit3 size={11} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Specialities strip */}
      {specialities.length > 0 && (
        <div className="mb-4">
          <div className="label-eyebrow mb-2">Specialities</div>
          <div className="flex flex-wrap gap-2" data-testid="tech-spec-chips">
            <button
              data-testid="tech-spec-chip-all"
              onClick={() => setSpecFilter("all")}
              className={`nb-btn px-3 py-1.5 text-sm ${
                specFilter === "all" ? "nb-btn-primary" : "bg-white"
              }`}
            >
              All
            </button>
            {specialities.map((s) => (
              <button
                key={s}
                data-testid={`tech-spec-chip-${s}`}
                onClick={() => setSpecFilter(s)}
                className={`nb-btn px-3 py-1.5 text-sm ${
                  specFilter === s ? "nb-btn-primary" : "bg-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative mb-6">
        <Search
          size={20}
          strokeWidth={2.5}
          className="absolute left-3.5 top-1/2 -translate-y-1/2"
        />
        <input
          data-testid="tech-search-input"
          className="nb-input pl-12 h-14 text-lg"
          placeholder="Search by name, org, role, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="techs-grid"
      >
        {filtered.map((t) => (
          <div
            key={t.id}
            className="nb-card p-5 flex flex-col gap-2"
            data-testid={`tech-card-${t.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl truncate">{t.name}</div>
                {t.role && (
                  <div className="text-sm text-[#607087] truncate">{t.role}</div>
                )}
                {t.supplier_name && (
                  <div className="text-xs font-bold text-[#228EBF] truncate mt-0.5">
                    {t.supplier_name}
                  </div>
                )}
              </div>
              {isManager && (
                <button
                  data-testid={`tech-edit-${t.id}`}
                  onClick={() => {
                    setEditingTech(t);
                    setTechFormOpen(true);
                  }}
                  className="nb-btn bg-white p-2"
                  aria-label="edit"
                >
                  <Edit3 size={14} strokeWidth={2.5} />
                </button>
              )}
            </div>
            {t.phone && (
              <a
                data-testid={`tech-tap-call-${t.id}`}
                href={`tel:${t.phone}`}
                className="flex items-center gap-2 text-sm font-bold hover:underline"
              >
                <Phone size={14} strokeWidth={2.5} className="text-[#228EBF]" /> {t.phone}
              </a>
            )}
            {t.email && (
              <a
                data-testid={`tech-tap-email-${t.id}`}
                href={`mailto:${t.email}`}
                className="flex items-center gap-2 text-sm hover:underline break-all"
              >
                <Mail size={14} strokeWidth={2.5} /> {t.email}
              </a>
            )}
            {t.after_hours_phone && (
              <a
                href={`tel:${t.after_hours_phone}`}
                className="flex items-center gap-2 text-xs text-[#7A4A00] font-bold hover:underline"
              >
                <Clock size={12} strokeWidth={2.5} /> After-hours: {t.after_hours_phone}
              </a>
            )}
            {t.specialities?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {t.specialities.map((s) => (
                  <span
                    key={s}
                    className="text-[10px] px-1.5 py-0.5 border-2 border-black rounded bg-[#EAF5FB] font-bold"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
            <button
              data-testid={`tech-view-machines-${t.id}`}
              onClick={() => setContactFor({ technicianId: t.id })}
              className="mt-2 nb-btn bg-white px-3 py-2 flex items-center justify-between gap-2 text-sm"
            >
              <span className="flex items-center gap-2">
                <Cog size={14} strokeWidth={2.5} />
                {t.linked_machine_count || 0} machine
                {(t.linked_machine_count || 0) === 1 ? "" : "s"}
              </span>
              <span className="text-xs text-[#607087]">View</span>
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full nb-card p-10 text-center">
            <div className="font-display text-2xl">
              {techs.length === 0 ? "No technicians yet" : "No matches"}
            </div>
            <div className="text-sm text-[#607087] mt-1">
              {techs.length === 0
                ? "Add your first service contact so staff can dial support without leaving the app."
                : "Try clearing the org or speciality filter."}
            </div>
          </div>
        )}
      </div>

      <TechnicianFormDialog
        open={techFormOpen}
        initial={editingTech}
        organisations={orgs}
        onClose={() => setTechFormOpen(false)}
        onSaved={load}
        onDelete={load}
        onAddOrganisation={() => {
          setEditingOrg(null);
          setOrgFormOpen(true);
        }}
      />

      <SupplierFormDialog
        open={orgFormOpen}
        initial={editingOrg}
        onClose={() => setOrgFormOpen(false)}
        onSaved={load}
        onDeleted={load}
      />

      <TechContactCard
        open={!!contactFor}
        technicianId={contactFor?.technicianId}
        onClose={() => setContactFor(null)}
      />

      <ManageTechnicianSpecialitiesDialog
        open={manageSpecOpen}
        onClose={() => setManageSpecOpen(false)}
        onChanged={load}
      />
    </div>
  );
};

export default Technicians;
